import { cva } from "cva";
import { useMemo } from "react";
import {
  Slider as AriaSlider,
  SliderProps as AriaSliderProps,
  SliderOutput,
  type SliderRenderProps,
  SliderThumb,
  SliderTrack,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Label } from "./Field";
import { composeTailwindRenderProps, focusRing } from "./utils";

const thumbStyles = cva({
  base: "top-1/2 size-5 rounded-full cursor-pointer shadow-[0_0_5.4px_5px_#BCBCBC2B] transition-colors duration-200",
  variants: {
    isDisabled: {
      true: "border-gray-300",
    },
    isInvalid: {
      true: "bg-gray-400",
      false: "bg-theme-primary",
    },
  },
  defaultVariants: {
    isInvalid: false,
  },
});

export interface SliderProps<T> extends AriaSliderProps<T> {
  label?: string;
  thumbLabels?: string[];
  thumbTooltip?: (value: number) => string;
  sliderOutput?: (props: SliderRenderProps) => string | null;

  minValidValue?: number;
  onValidationChange?: (isValid: boolean) => void;

  progressStyle?: "default" | "theme";
}

export function Slider<T extends number | number[]>({
  label,
  thumbLabels,
  sliderOutput,
  thumbTooltip,
  minValidValue,
  onValidationChange,
  progressStyle = "default",
  minValue = 0,
  maxValue = 100,
  ...props
}: SliderProps<T>) {
  const zeroPointPercentage = useMemo(() => {
    if (minValue >= 0) return 0;
    if (maxValue <= 0) return 1;
    const range = Math.abs(minValue) + Math.abs(maxValue);
    return (Math.abs(minValue) / range) * 1;
  }, [minValue, maxValue]);

  const progressBackground = (state: SliderRenderProps["state"]) => {
    if (state.values.length > 1) return "transparent";

    const thumbPercentage = state.getThumbPercent(0);

    if (thumbPercentage === zeroPointPercentage) return "transparent";

    const isInvalid =
      minValidValue !== undefined && state.values[0] < minValidValue;

    const stops =
      progressStyle === "theme"
        ? [
            `transparent ${zeroPointPercentage * 100}%`,
            `transparent ${zeroPointPercentage * 100}%`,
            `var(--color-${!isInvalid ? "theme-primary" : "gray-400"}) ${thumbPercentage * 100}%`,
            `transparent ${thumbPercentage * 100}%`,
          ]
        : [
            `transparent ${zeroPointPercentage * 100}%`,
            `var(--color-theme-on-background) ${zeroPointPercentage * 100}%`,
            `var(--color-theme-on-background) ${(zeroPointPercentage + (thumbPercentage - zeroPointPercentage) * 0.4) * 100}%`,
            `transparent ${thumbPercentage * 100}%`,
          ];

    if (thumbPercentage < zeroPointPercentage) stops.reverse();

    return `linear-gradient(
      to right,
      ${stops.join(", ")}
    )`;
  };

  return (
    <AriaSlider
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "flex w-full flex-col items-center gap-y-4",
      )}
      minValue={minValue}
      maxValue={maxValue}
      onChange={(value) => {
        props.onChange?.(value);

        if (minValidValue !== undefined && onValidationChange) {
          const currentValue = Array.isArray(value) ? value[0] : value;
          if (typeof currentValue === "number") {
            onValidationChange(currentValue >= minValidValue);
          }
        }
      }}
    >
      {({ state }) => {
        const isValid = (value: number) =>
          minValidValue === undefined || value >= minValidValue;

        return (
          <>
            {label && <Label>{label}</Label>}

            {thumbTooltip && (
              <div className="relative w-full shrink-0 text-xs font-medium">
                <div className="select-none">&nbsp;</div>

                {state.values.map((value, i) => (
                  <div
                    key={`tooltip-${i}`}
                    className="pointer-events-none absolute top-0 -translate-x-1/2 whitespace-nowrap"
                    style={{
                      left: `${state.getThumbPercent(i) * 100}%`,
                    }}
                  >
                    {thumbTooltip(value)}
                  </div>
                ))}
              </div>
            )}

            {/* We keep the non-visual track height 13px so it's clickable */}
            <SliderTrack className="group relative col-span-2 flex h-[13px] w-full items-center">
              {({ state }) => (
                <>
                  {/* Track */}
                  <div className="bg-theme-outline h-px w-full" />

                  {/* Fill */}
                  <div
                    className="absolute inset-x-0 h-px"
                    style={{
                      background: progressBackground(state),
                    }}
                  />

                  {state.values.length === 1 &&
                    zeroPointPercentage !== 0 &&
                    zeroPointPercentage !== 1 &&
                    state.getThumbPercent(0) !== zeroPointPercentage && (
                      <div
                        className="bg-theme-outline absolute w-px -translate-x-1/2"
                        style={{
                          left: `${zeroPointPercentage * 100}%`,
                          height: `${Math.min(
                            26,
                            Math.abs(
                              zeroPointPercentage - state.getThumbPercent(0),
                            ) * 200,
                          )}px`,
                        }}
                      />
                    )}

                  {state.values.map((_, i) => (
                    <SliderThumb
                      key={i}
                      index={i}
                      aria-label={thumbLabels?.[i]}
                      className={(renderProps) =>
                        twMerge(
                          thumbStyles({
                            ...renderProps,
                            isInvalid: !isValid(state.values[i]),
                          }),
                          focusRing(renderProps),
                        )
                      }
                    />
                  ))}
                </>
              )}
            </SliderTrack>

            {sliderOutput && (
              <SliderOutput
                className={twMerge(
                  "text-center text-[11px] font-bold transition-opacity",
                  state.values[0]
                    ? "opacity-100 duration-300"
                    : "opacity-0 duration-0",
                )}
              >
                {(sliderProps) => sliderOutput(sliderProps)}
              </SliderOutput>
            )}
          </>
        );
      }}
    </AriaSlider>
  );
}
